/** Map react-admin form record ↔ .NET UpsertFbrInvoiceRequest. */

import type { TaxLineInfo } from '../workshop/jobEstimate/jobEstimateCalc';
import { computeFbrInvoiceLineAmounts } from './fbrInvoiceCalc';

export type FbrInvoiceLineForm = {
    id?: string;
    productProfileId?: string;
    productNo?: string;
    productName?: string;
    quantity?: number;
    unitPrice?: number;
    taxRate?: number;
    fbrSalesTaxRateId?: number | null;
    /** Multiple FBR sales taxes; chips in UI */
    fbrSalesTaxRateIds?: number[];
    /** Per-tax settings metadata (display calc only; refilled from ids on load, never sent). */
    taxLineMeta?: TaxLineInfo[];
    /** Discount percent (0-100) */
    discountRate?: number;
    /** Fixed notified / MRP-based tax flag (may be overridden per invoice line) */
    fixedNotifiedApplicable?: boolean;
    /** MRP rate value used as tax base when fixed notified applies */
    mrpRateValue?: number;
    hsCode?: string;
    /** Legacy DB snapshot; not shown in invoice line grid */
    sroItemText?: string;
    /** Product profile free-text (display / optional column) */
    sroScheduleNoText?: string;
    sroItemRefText?: string;
    /** PDI transaction type description (FBR sale type) */
    fbrSaleTypeText?: string;
    remarks?: string;

    taxAmount?: number;
    subtotal?: number;
    totalAmount?: number;
};

function toDateOnlyLocal(value: unknown): string | undefined {
    if (value == null || value === '') return undefined;
    if (typeof value === 'string') {
        // Accept already-normalized yyyy-MM-dd (preferred)
        if (/^\d{4}-\d{2}-\d{2}$/.test(value.trim())) return value.trim();
        // If server ever returns an ISO-ish string, keep date portion only
        if (value.length >= 10 && /^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
        return undefined;
    }
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
        const y = value.getFullYear();
        const m = String(value.getMonth() + 1).padStart(2, '0');
        const d = String(value.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    }
    return undefined;
}

function toIsoUtc(value: unknown): string | undefined {
    if (value == null || value === '') return undefined;
    if (typeof value === 'string') return value;
    if (value instanceof Date) return value.toISOString();
    return undefined;
}

function lineHasProduct(l: FbrInvoiceLineForm | undefined): boolean {
    const id = l?.productProfileId;
    if (id == null || id === '') return false;
    return String(id).replace(/-/g, '').length > 0;
}

/** Normalize API record so nested lines register correctly in react-hook-form (Guids, decimals). */
export function normalizeFbrInvoiceRecord(data: Record<string, unknown> | null | undefined) {
    if (!data || typeof data !== 'object') return data;
    const raw = (data.lines as FbrInvoiceLineForm[] | undefined) ?? [];
    const lines = raw.map(l => {
        const apiIds = (l as any).fbrSalesTaxRateIds as unknown;
        let fbrSalesTaxRateIds: number[] = [];
        if (Array.isArray(apiIds) && apiIds.length > 0) {
            fbrSalesTaxRateIds = apiIds.map(x => Number(x)).filter(x => Number.isFinite(x) && x > 0);
        } else if (
            (l as any).fbrSalesTaxRateId != null &&
            (l as any).fbrSalesTaxRateId !== ''
        ) {
            const one = Number((l as any).fbrSalesTaxRateId);
            if (Number.isFinite(one) && one > 0) fbrSalesTaxRateIds = [one];
        }
        return {
            id: l.id != null ? String(l.id) : undefined,
            productProfileId:
                l.productProfileId != null && l.productProfileId !== ''
                    ? String(l.productProfileId)
                    : '',
            productNo: l.productNo,
            productName: l.productName,
            quantity: Number(l.quantity) || 0,
            unitPrice: Number(l.unitPrice) || 0,
            taxRate: Number(l.taxRate) || 0,
            fbrSalesTaxRateId:
                (l as any).fbrSalesTaxRateId == null || (l as any).fbrSalesTaxRateId === ''
                    ? null
                    : Number((l as any).fbrSalesTaxRateId),
            fbrSalesTaxRateIds,
            taxLineMeta: [],
            discountRate: Number((l as any).discountRate) || 0,
            fixedNotifiedApplicable: Boolean((l as any).fixedNotifiedApplicable),
            mrpRateValue: Number((l as any).mrpRateValue) || 0,
            hsCode: l.hsCode ?? '',
            sroItemText: l.sroItemText ?? '',
            sroScheduleNoText: String((l as any).sroScheduleNoText ?? ''),
            sroItemRefText: String((l as any).sroItemRefText ?? ''),
            fbrSaleTypeText: String((l as any).fbrSaleTypeText ?? ''),
            remarks: l.remarks ?? '',
            // Read by print only (never sent back): FBR UoM label and the stored net / gross amounts.
            uomText: String((l as any).uomText ?? ''),
            netAmount: (l as any).netAmount != null ? Number((l as any).netAmount) : undefined,
            grossAmount: (l as any).grossAmount != null ? Number((l as any).grossAmount) : undefined,
        };
    });
    const topId = (data as any).id ?? (data as any).Id;
    return {
        ...data,
        ...(topId != null && topId !== '' ? { id: String(topId) } : {}),
        lines,
        invoiceNumber: (data as any).invoiceNumber != null ? String((data as any).invoiceNumber) : '',
        reference:
            (data as any).reference == null || (data as any).reference === ''
                ? ''
                : String((data as any).reference),
        isLocked: Boolean((data as any).isLocked),
        fbrInvoiceNumber: (data as any).fbrInvoiceNumber ?? undefined,
        validatedAtUtc: (data as any).validatedAtUtc,
        postedAtUtc: (data as any).postedAtUtc,
        fbrLastError: (data as any).fbrLastError ?? null,
        fbrLastResponseJson: (data as any).fbrLastResponseJson ?? null,
        fbrScenarioId: (data as any).fbrScenarioId ?? null,
        subPartyId: Number((data as any).subPartyId) > 0 ? Number((data as any).subPartyId) : null,
        workOrderNumber:
            (data as any).workOrderNumber == null ? '' : String((data as any).workOrderNumber),
        workOrderDate: toDateOnlyLocal((data as any).workOrderDate) ?? null,
    };
}

export function mapFbrInvoiceToUpsertBody(data: Record<string, unknown>) {
    const linesRaw = (data.lines as FbrInvoiceLineForm[] | undefined) ?? [];
    const lines = linesRaw
        .filter(lineHasProduct)
        .map(l => {
            const fromArr =
                Array.isArray(l.fbrSalesTaxRateIds) && l.fbrSalesTaxRateIds.length > 0
                    ? l.fbrSalesTaxRateIds.map(x => Number(x)).filter(x => Number.isFinite(x) && x > 0)
                    : l.fbrSalesTaxRateId != null && Number(l.fbrSalesTaxRateId) > 0
                      ? [Number(l.fbrSalesTaxRateId)]
                      : [];

            // Tax is NOT sent as an authoritative figure. The API holds the same fbrSalesTaxRates
            // configuration this grid reads and runs the identical formula
            // (FbrLineTaxCalculator), so it computes net/tax/gross and the per-tax breakdown
            // itself and the stored numbers match the screen by construction.
            //
            // taxAmount below is a legacy fallback the API uses ONLY when a line's rate ids no
            // longer resolve to master rows. Deliberately not the source of truth: taxLineMeta is
            // blanked on record load and repopulated asynchronously, so this value silently
            // degrades to an MRP-based estimate whenever it is read before that lands — which is
            // how 1,128.80 once reached the database for INV00168.
            const amounts = computeFbrInvoiceLineAmounts({
                quantity: Number(l.quantity) || 0,
                unitPrice: Number(l.unitPrice) || 0,
                discountRate: Number((l as any).discountRate) || 0,
                taxRate: Number(l.taxRate) || 0,
                taxLineMeta: Array.isArray((l as any).taxLineMeta)
                    ? ((l as any).taxLineMeta as TaxLineInfo[])
                    : [],
                fixedNotifiedApplicable: Boolean((l as any).fixedNotifiedApplicable),
                mrpRateValue: Number((l as any).mrpRateValue) || 0,
            });

            return {
                productProfileId: String(l.productProfileId),
                quantity: Number(l.quantity) || 0,
                unitPrice: Number(l.unitPrice) || 0,
                taxRate: Number(l.taxRate) || 0,
                fbrSalesTaxRateId:
                    fromArr.length > 0 ? fromArr[0] : l.fbrSalesTaxRateId == null ? null : Number(l.fbrSalesTaxRateId),
                fbrSalesTaxRateIds: fromArr.length > 0 ? fromArr : undefined,
                discountRate: Number((l as any).discountRate) || 0,
                // Always send explicit FN/MRP so invoice overrides persist (false must not fall back to product profile).
                fixedNotifiedApplicable: Boolean((l as any).fixedNotifiedApplicable),
                mrpRateValue: Number((l as any).mrpRateValue) || 0,
                remarks: String(l.remarks ?? ''),
                taxAmount: amounts.taxAmount,
            };
        });

    const fbrScn = (data as any).fbrScenarioId;

    return {
        reference: data.reference as string | undefined,
        customerPartyId: Number(data.customerPartyId) || 0,
        // Branch of the customer; null = billed to the customer itself.
        subPartyId: Number((data as any).subPartyId) > 0 ? Number((data as any).subPartyId) : null,
        // Always round-tripped (even while the setting hides the inputs) so turning the setting
        // off never wipes values already saved on an invoice.
        workOrderNumber: String((data as any).workOrderNumber ?? '').trim() || null,
        workOrderDate: toDateOnlyLocal((data as any).workOrderDate) ?? null,
        // Date-only to avoid UTC shifting; backend persists DATE and uses it for FBR payload.
        invoiceDate: toDateOnlyLocal((data as any).invoiceDate),
        paymentTerms: (data.paymentTerms as string) ?? 'immediate',
        status: (data.status as string) ?? 'ordered',
        returned: false,
        deliveryFees: Number(data.deliveryFees) || 0,
        fbrScenarioId:
            fbrScn === '' || fbrScn === undefined || fbrScn === null ? null : Number(fbrScn),
        lines,
    };
}
